import React, {useState, useEffect, useRef, useMemo, useCallback} from 'react';
import {isEmpty} from 'lodash';
import {isValidPhoneNumber} from 'react-phone-number-input/input';

import getCapitalizedLocale from '../../../../../platform/src/helpers/get-capitalized-locale';
import {checkEmail, registrationByEmail} from '../../../../../player/js/api';
import * as widgetConstants from "../../core/constants";
import * as settingsConstants from '../../../../../cp/components/EventPage/Widgets/NewRegistration/constants';
import {registrationErrors, responseErrorsMessage} from '../../core/models';
import filterFilledRegistationFields from '../../core/helpers/filterFilledRegistrationFields';
import checkFormValidity from '../../core/utils/checkFormValidity';
import getRegistrationFields from '../../core/helpers/updateRegistrationFields';
import replacePlaceholders from '../../core/helpers/replacePlaceholders';

import InputKonturFocus from '../../../../../platform/src/components/Authentication/InputKonturFocus';
import InputField from '../../../../../platform/src/components/Authentication/InputField';
import UploadPhotoField from '../UploadPhotoField';
import UploadPhotoEditor from '../UploadPhotoField/UploadPhotoEditor';
import UIButton from '../../../../../globalComponents/UIComponents/UIButton';

import './styles.scss';
import { useEventContext } from '../../context/EventContext';

export default ({
    eventKey,
    settings,
    participationType,
    yaCode,
    accessPreModeration,
    $locale,
    params,
    onError,
    onSuccess,
    widgetFields,
    placeholdersData,
    corporateMail,
    registrationType,
}) => {
    const formRef = useRef();
    const { event } = useEventContext();

    const [registrationFields, setRegistrationFields] = useState([]);
    const [disclaimerFields, setDisclaimerFields] = useState([]);
    const [info, setInfo] = useState({});
    const [errors, setErrors] = useState({});
    const [disabled, setDisabled] = useState(false);
    const [isValidForm, setValidForm] = useState(false);
    const [isFormErrorMessageVisible, setFormErrorMessageVisible] = useState(false);
    const [isRegisteredMember, setRegisteredMember] = useState(false);
    const [isPhotoEditorVisible, setPhotoEditorVisible] = useState(false);
    const [uploadedPhoto, setUploadedPhoto] = useState(null);
    const [initialPhoto, setInitialPhoto] = useState(null);
    const [isViewingPhoto, setViewingPhoto] = useState(false);
    const [isParticipationTypeDisabled, setParticipationTypeDisabled] = useState(false);

    const isHybridType = registrationType === widgetConstants.TYPE_HYBRID && participationType === widgetConstants.TYPE_HYBRID;
    const locale = getCapitalizedLocale($locale.name);

    const title = replacePlaceholders(settings?.[`${settingsConstants.WIDGET_FIRST_SCREEN_TITLE}${locale}`], placeholdersData) || '';
    const subtitle = replacePlaceholders(settings?.[`${settingsConstants.WIDGET_FIRST_SCREEN_SUBTITLE}${locale}`], placeholdersData) || '';

    const resultFields = [...registrationFields, ...disclaimerFields];

    const updateDefaultValues = useCallback((registrationFields) => {
        if (!info || !registrationFields?.length) return;
        const _info = {};
        registrationFields.forEach(field => {
            if (field.defaultValue && !info[field.name]) {
                _info[field.name] = field.defaultValue
            }
        })
        setInfo(prev => ({...prev, ..._info}));
    }, [info])

    useEffect(() => {
        updateDefaultValues(registrationFields);
    }, [registrationFields])

    useEffect(() => {
        if (!isHybridType) {
            setInfo((prevState) => ({...prevState, participationType: registrationType}));
        }

        if (yaCode && window['yaCounter' + yaCode]) {
            window['yaCounter' + yaCode]['reachGoal']('[COMDI] widget loaded form');
        }
    }, []);

    /** Получение и обработка полей в зависимости от локали */
    const initialFields = useMemo(() => {
        const widgetFieldsFilter = widgetFields?.filter(({ field }) => !field || field.type !== widgetConstants.FIELD_DISCLAIMER);
        if (!widgetFieldsFilter) return;

        return getRegistrationFields(widgetFieldsFilter, $locale.name, event);
    }, [widgetFields]);

    const initialDisclaimerFields = useMemo(() => {
        const widgetFieldsFilter = widgetFields?.filter(({ field }) => field && field.type === widgetConstants.FIELD_DISCLAIMER);
        if (!widgetFieldsFilter) return;

        return getRegistrationFields(widgetFieldsFilter, $locale.name, event);
    }, [widgetFields]);

    useEffect(() => {
        setRegistrationFields(initialFields);
    }, [initialFields]);

    useEffect(() => {
        setDisclaimerFields(initialDisclaimerFields);
    }, [initialDisclaimerFields]);

    useEffect(() => {
        setValidForm(checkFormValidity(info, resultFields) && isEmpty(errors));

        if (isValidForm && yaCode && window['yaCounter' + yaCode]) {
            window['yaCounter' + yaCode]['reachGoal']('[COMDI] widget edited field');
        };
    }, [info]);

    /** Показывать сообщение Заполните обязательные поля, если есть ошибки в полях */
    useEffect(() => {
        setFormErrorMessageVisible(!isEmpty(errors) && !isValidForm);
    }, [errors, info, isValidForm]);

    /** Следить за полем Почта и убирать дизейбл с Формата участия, если она введена */
    useEffect(() => {
        if (!info[widgetConstants.FIELD_EMAIL]) return;

        setErrors(({[widgetConstants.FIELD_EMAIL]: _, ...errors}) => errors);
        setParticipationTypeDisabled(false);
    }, [info[widgetConstants.FIELD_EMAIL]]);

    /** Проверять высоту контента, чтобы iframe подстраивался по высоте */
    function setWindowHeight() {
        const height = window.document.body.offsetHeight;
        window.parent.postMessage({setHeight: height}, '*');
    }

    /** Проверять почту и показывать ошибки в зависимости от сценария */
    const handleCheckEmail = async (field) => {
        if (field !== widgetConstants.FIELD_EMAIL) return;

        try {
            const response = await checkEmail(eventKey, {...info, sendPassword: false});
            const {requiredFields, status, registered} = response;

            if (requiredFields?.length) {
                setRegistrationFields(
                    filterFilledRegistationFields(requiredFields, initialFields),
                );
                setDisclaimerFields(
                    filterFilledRegistationFields(requiredFields, initialDisclaimerFields),
                );
                if (response.participationType) {
                    setInfo((prevState) => ({...prevState, participationType: response.participationType}));
                    setParticipationTypeDisabled(true);
                }
                setErrors({});
                setRegisteredMember(!!registered);

                return;
            }

            if (status) {
                setErrors({...errors, [field]: registrationErrors[widgetConstants.STATUS_ALREADY_REGISTERED]});

                return;
            };

            setRegistrationFields(initialFields);
            setDisclaimerFields(initialDisclaimerFields);
            setRegisteredMember(false);
        } catch (error) {
            console.error(error);
            const {message} = error.data.error;

            if (message === $locale.get(responseErrorsMessage[widgetConstants.STATUS_NO_ACCESS])) {
                setErrors({
                    ...errors,
                    [field]: corporateMail
                        ? registrationErrors[widgetConstants.STATUS_USE_CORPORATE_MAIL]
                        : registrationErrors[widgetConstants.STATUS_NO_ACCESS],
                });
            }

            if (message === $locale.get(responseErrorsMessage[widgetConstants.STATUS_WRONG_FORMAT])) {
                setErrors({ ...errors, [field]: registrationErrors[widgetConstants.STATUS_WRONG_FORMAT]});
            }

            if (message === $locale.get(responseErrorsMessage[widgetConstants.STATUS_NOT_SPECIFIED])) {
                setErrors({ ...errors, [field]: registrationErrors[widgetConstants.STATUS_NOT_SPECIFIED]});
                setRegistrationFields(initialFields);
                setDisclaimerFields(initialDisclaimerFields);
                setRegisteredMember(false);
            }
        } finally {
            setWindowHeight();
        }
    };

    /** Проверять поля на валидность и выводить ошибки */
    const handleSetError = (field) => {
        if (!field.required) return;

        const value = info[field.name] || null;

        if (!info[field.name] || typeof value === 'string' && !value.trim() || !value) {
            setErrors((prevState) => ({
                ...prevState,
                [field.name]: registrationErrors[widgetConstants.STATUS_NOT_SPECIFIED],
            }));

            return;
        }

        const regexp = new RegExp(field.regexp) || '';
        if (field.regexp && !regexp.test(value)) {
            setErrors((prevState) => ({
                ...prevState,
                [field.name]: registrationErrors[widgetConstants.STATUS_WRONG_FORMAT],
            }));
        }

        if (field.type === widgetConstants.FIELD_TEL && !isValidPhoneNumber(value)) {
            setErrors((prevState) => ({
                ...prevState,
                [field.name]: registrationErrors[widgetConstants.STATUS_WRONG_FORMAT],
            }));
        }
    };

    /** Если поле Почта не заполнена, дизейблить поле Формат участия */
    const handleCheckParticipationType = (field) => {
        if (field !== widgetConstants.FIELD_PARTICIPATION_TYPE) return;

        if (!info[widgetConstants.FIELD_EMAIL]) {
            setErrors({...errors, [widgetConstants.FIELD_EMAIL]: registrationErrors[widgetConstants.STATUS_ENTER_EMAIL]});
            setParticipationTypeDisabled(true);
        }
    };

    /** Проверять все поля на валидность, выводить ошибки в полях, если они не заполнены */
    const handleCheckFieldValid = () => {
        setFormErrorMessageVisible(!checkFormValidity(info, resultFields) || !isEmpty(errors));
        registrationFields.forEach((field) => handleSetError(field));
    };

    const handleSavePhoto = (value) => {
        setInfo({...info, [widgetConstants.FIELD_PHOTO]: value});
        setUploadedPhoto(value);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (yaCode && window['yaCounter' + yaCode]) {
            window['yaCounter' + yaCode]['reachGoal']('[COMDI] widget clicked button');
        }

        if (!checkFormValidity(info, resultFields) || !isEmpty(errors)) {
            return;
        }

        try {
            setDisabled(true);

            const payload = {
                ...info,
                locale: $locale.name,
                location: decodeURIComponent(params.parentUrl ?? ''),
                sendPassword: false,
            };

            const formData = new FormData();
            Object.keys(payload).forEach(key => {
                if (key === 'konturFocusOrg') {
                    ['organizationInn', 'organizationKpp', 'organizationName'].forEach(key => formData.append(`konturFocusOrg[${key}]`, info.konturFocusOrg[key] || null));
                    return;
                }

                formData.append(key, typeof payload[key] === 'string' ? payload[key].trim() : payload[key]);
            });

            const response = await registrationByEmail(eventKey, formData);
            await onSuccess({email: response.email});
        } catch (e) {
            setDisabled(false);
            console.error(e);
            let errors = {};
            e.data?.form?.forEach((name) => errors[name] = true);
            onError(errors);
        }
    };

    return (
        <div className="registration__content">
            {
                !isPhotoEditorVisible
                ? <>
                    {
                        !settings?.[settingsConstants.WIDGET_FIRST_SCREEN_TITLE_HIDDEN] &&
                        <h1 className="registration__title">
                            {title || $locale.get('registration.title')}
                        </h1>
                    }
                    {
                        (!settings?.[settingsConstants.WIDGET_FIRST_SCREEN_SUBTITLE_HIDDEN] && !!subtitle) &&
                        <p className="registration__subtitle">
                            {subtitle}
                        </p>
                    }
                    {
                        isRegisteredMember &&
                        <p className="registration__form-error-message visible">
                            {$locale.get('registration.fillMissingInfo')}
                        </p>
                    }
                    <form
                        ref={formRef}
                        className="registration__form"
                        noValidate
                        onSubmit={handleSubmit}
                    >
                        <div className={`registration__form-fields ${settings?.[settingsConstants.WIDGET_COLUMNS_COUNT] === '2' ? ' two-column' : ''}`}>
                            {registrationFields.map((field) => (
                                ((field.name === 'participationType' && isHybridType) || field.name !== 'participationType') &&
                                <div
                                    key={field.id}
                                    className={`${settings?.[settingsConstants.WIDGET_FIELDS_PERIMETER_BORDER] === 'bottom' ? ' bottom-border' : ''} ${errors[field.name] ? ' field-error' : ''}`}
                                >
                                    {
                                        field.type === widgetConstants.FIELD_PHOTO &&
                                        <UploadPhotoField
                                            $locale={$locale}
                                            required={field.required}
                                            disabled={disabled}
                                            isPhotoUpload={!!info[widgetConstants.FIELD_PHOTO]}
                                            onOpenEditor={() => setPhotoEditorVisible(true)}
                                            onView={() => setViewingPhoto(true)}
                                        />
                                    }
                                    {
                                        field.type === widgetConstants.FIELD_KONTUR_FOCUS &&
                                        <InputKonturFocus
                                            {...field}
                                            $locale={$locale}
                                            eventKey={eventKey}
                                            label={
                                                (!!field.label && !settings?.[settingsConstants.WIDGET_FIRST_SCREEN_LABELS_HIDDEN])
                                                    ? field.label
                                                    : ''
                                            }
                                            placeholder={
                                                (!!field.placeholder && !settings?.[settingsConstants.WIDGET_FIRST_SCREEN_PLACEHOLDERS_HIDDEN])
                                                    ? field.placeholder
                                                    : ''
                                            }
                                            error={errors[field.name]?.hasError}
                                            disabled={disabled}
                                            onChange={(value) => {
                                                setInfo({...info, [field.name]: value});
                                                setErrors(({[field.name]: _, ...errors}) => errors);
                                            }}
                                            onBlur={() => handleSetError(field.name)}
                                        />
                                    }
                                    {
                                        (field.type !== widgetConstants.FIELD_PHOTO && field.type !== widgetConstants.FIELD_KONTUR_FOCUS) &&
                                        <InputField
                                            {...field}
                                            className={(errors[field.name] ? 'input-error' : '')}
                                            value={info[field.name]}
                                            label={
                                                (!!field.label && !settings?.[settingsConstants.WIDGET_FIRST_SCREEN_LABELS_HIDDEN])
                                                    ? field.label
                                                    : ''
                                            }
                                            placeholder={
                                                (!!field.placeholder && !settings?.[settingsConstants.WIDGET_FIRST_SCREEN_PLACEHOLDERS_HIDDEN])
                                                    ? field.placeholder
                                                    : ''
                                            }
                                            disabled={disabled}
                                            $locale={$locale}
                                            isOptionsDisabled={field.name === widgetConstants.FIELD_PARTICIPATION_TYPE && isParticipationTypeDisabled}
                                            error={errors[field.name]?.hasError}
                                            errorName={errors[field.name]?.name ? $locale.get(errors[field.name].name) : ''}
                                            defaultOptionTitle={!!field.placeholder ? $locale.get('input.select.defaultTitle') : ''}
                                            color={info[field.name] ? settings.inputTextColor : settings.placeholderColor}
                                            hasAlignment={settings?.[settingsConstants.WIDGET_COLUMNS_COUNT] === '2'}
                                            onFocus={() => handleCheckParticipationType(field.name)}
                                            onChange={(value) => {
                                                setInfo({...info, [field.name]: value});
                                                setErrors(({[field.name]: _, ...errors}) => errors);
                                            }}
                                            onBlur={() => {
                                                handleCheckEmail(field.name);
                                                handleSetError(field);
                                            }}
                                        />
                                    }
                                </div>
                            ))}
                        </div>
                        <div className={`registration__form-fields`}>
                            {disclaimerFields.map((field) => (
                                ((field.name === 'participationType' && isHybridType) || field.name !== 'participationType') &&
                                <div
                                    key={field.id}
                                    className={`${settings?.[settingsConstants.WIDGET_FIELDS_PERIMETER_BORDER] === 'bottom' ? ' bottom-border' : ''} ${errors[field.name] ? ' field-error' : ''}`}
                                >
                                    <InputField
                                        {...field}
                                        className={(errors[field.name] ? 'input-error' : '')}
                                        value={info[field.name]}
                                        label={
                                            (!!field.label && !settings?.[settingsConstants.WIDGET_FIRST_SCREEN_LABELS_HIDDEN])
                                                ? field.label
                                                : ''
                                        }
                                        placeholder={
                                            (!!field.placeholder && !settings?.[settingsConstants.WIDGET_FIRST_SCREEN_PLACEHOLDERS_HIDDEN])
                                                ? field.placeholder
                                                : ''
                                        }
                                        disabled={disabled}
                                        $locale={$locale}
                                        isOptionsDisabled={field.name === widgetConstants.FIELD_PARTICIPATION_TYPE && isParticipationTypeDisabled}
                                        error={errors[field.name]?.hasError}
                                        errorName={errors[field.name]?.name ? $locale.get(errors[field.name].name) : ''}
                                        defaultOptionTitle={!!field.placeholder ? $locale.get('input.select.defaultTitle') : ''}
                                        color={info[field.name] ? settings.inputTextColor : settings.placeholderColor}
                                        hasAlignment={settings?.[settingsConstants.WIDGET_COLUMNS_COUNT] === '2'}
                                        onFocus={() => handleCheckParticipationType(field.name)}
                                        onChange={(value) => {
                                            setInfo({...info, [field.name]: value});
                                            setErrors(({[field.name]: _, ...errors}) => errors);
                                        }}
                                        onBlur={() => {
                                            handleCheckEmail(field.name);
                                            handleSetError(field);
                                        }}
                                        isDisclaimer
                                    />
                                </div>
                            ))}
                        </div>
                        <p className={`registration__form-error-message ${(isFormErrorMessageVisible) ? 'visible' : ''}`}>
                            *{$locale.get('auth.login.fillRequiredFields')}
                        </p>
                        <div style={{display: 'flex', justifyContent: settings?.[settingsConstants.WIDGET_PRIMARY_BUTTON_LOCATION]}}>
                            <UIButton
                                type="submit"
                                size="large"
                                style={`primary ${settings?.[settingsConstants.WIDGET_PRIMARY_BUTTON_FILLED] === 'bordered' ? 'bordered' : ''}`}
                                disabled={disabled}
                                onClick={handleCheckFieldValid}
                            >
                                {
                                    settings?.[`${settingsConstants.WIDGET_FIRST_SCREEN_BUTTON_TEXT}${locale}`]
                                    || $locale.get(`auth.login.button.${accessPreModeration ? 'sendRequest' : 'register'}`)
                                }
                            </UIButton>
                        </div>
                    </form>
                </>
            : <UploadPhotoEditor
                    eventKey={eventKey}
                    photo={uploadedPhoto}
                    initialPhoto={initialPhoto}
                    isViewingPhoto={isViewingPhoto}
                    onChange={handleSavePhoto}
                    onClose={() => setPhotoEditorVisible(false)}
                    onSetInitialPhoto={setInitialPhoto}
                />
            }
        </div>
    )
}
